import React, {useEffect, useState} from 'react';
import { Helmet } from 'react-helmet-async';
import { BsRouter } from "react-icons/bs";
import Footer from './Footer';
import logoImage from '../assets/Logo.png';
import { useNavigate } from 'react-router-dom';
import { useSprings, useSpring, animated } from 'react-spring';
import iqMap from '../assets/iqmap.svg';
import shield from '../assets/Safeshield.svg';
import { Fade } from 'react-reveal';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { FaShieldHalved } from "react-icons/fa6";
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';


const AntiDDos = () => {
    const navigate = useNavigate();
    const { lang } = useLanguage();
    const t = translations[lang];
    const isRTL = lang === 'ar';
    const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

    useEffect(() => {
        window.scrollTo(0, 0);
        const checkOrientation = () => {
            setIsPortrait(window.innerHeight > window.innerWidth);
        };
        window.addEventListener('resize', checkOrientation);
        checkOrientation();
        return () => {
            window.removeEventListener('resize', checkOrientation);
        };
    }, []);

    const handleHeaderClick = () => {
        navigate('/');
    };

    const center = isPortrait
    ? { x: '55%', y: '49%' }
    : { x: '55%', y: '48%' };

  const getRouters = (isPortrait) => {
    if (isPortrait) {
        return [
            { id: 1, x: '25%', y: '30%' },
            { id: 2, x: '68%', y: '50%' },
            { id: 3, x: '50%', y: '70%' },
            { id: 4, x: '38%', y: '15%' },
            { id: 5, x: '47%', y: '15%' },
            { id: 6, x: '62%', y: '30%' },
            { id: 7, x: '84%', y: '68%' },
        ];
    } else {
        return [
            { id: 1, x: '28%', y: '30%' },
            { id: 2, x: '20%', y: '50%' },
            { id: 3, x: '60%', y: '80%' },
            { id: 4, x: '38%', y: '5%' },
            { id: 5, x: '47%', y: '4%' },
            { id: 6, x: '60%', y: '30%' },
            { id: 7, x: '84%', y: '75%' },
        ];
    }
  };

  const routers = getRouters(isPortrait);

  const [routerSprings] = useSprings(routers.length, index => ({
    from: { opacity: 0, transform: 'scale(0)', color: 'red' },
    to: async (next) => {
        await next({ opacity: 1, transform: 'scale(1)' });
        await new Promise(resolve => setTimeout(resolve, 800 + 200 * index + 1000));
        await next({ color: '#438c11' });
    },
    delay: 50 * index,
  }));

    const calculateAngle = (x1, y1, x2, y2) => {
        const dy = y2 - y1;
        const dx = x2 - x1;
        return Math.atan2(dy, dx) * 180 / Math.PI;
    };

    const [lineSprings] = useSprings(routers.length, index => {
        const x1 = parseFloat(routers[index].x);
        const y1 = parseFloat(routers[index].y);
        const x2 = parseFloat(center.x);
        const y2 = parseFloat(center.y);
        const angle = calculateAngle(x1, y1, x2, y2);

        return {
            from: { left: `${x1}%`, top: `${y1}%`, width: '0%', backgroundColor: 'red', transform: `rotate(${angle}deg)` },
            to: async (next) => {
                await next({ width: `${Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)}%` });
                await new Promise(resolve => setTimeout(resolve, 800));
                await next({ backgroundColor: '#97d070' });
                await next({ left: `${x2}%`, top: `${y2}%`, width: '0%', transform: `rotate(${angle}deg) translateX(-50%)` });
            },
            delay: 200 * index + 1000,
        };
    });

    const shieldAnimation = useSpring({
        from: { opacity: 0, transform: 'scale(0)' },
        to: { opacity: 1, transform: 'scale(2)' },
        delay: 200 * routers.length + 1000,
    });

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{t.antiddos_title}</title>
        <meta name="description" content={t.antiddos_desc} />
      </Helmet>
      <div className="video-header" onClick={handleHeaderClick} style={{ height: '250px' }}>
        <video autoPlay loop muted className="background-video">
          <source src="/assets/cybernet.mp4" type="video/mp4" />
        </video>
        <div className="overlay"></div>
        <div className="header-content" style={{ padding: '20px' }}>
          <img src={logoImage} alt="Technology Resources Logo" className="header-logo" style={{ height: '80px' }} />
          <h1 className="header-title font-magistral" style={{ fontSize: '24px' }}>Tech Resources</h1>
        </div>
      </div>

      <div className="bg-white flex flex-col">
        <div className="flex-grow container mx-auto p-0 relative text-center">
          <Fade delay={400}>
            <div className="map-container" style={{ position: 'relative', display: 'inline-block', marginBottom: '1px' }}>
              <img src={iqMap} alt="Iraq Map" style={{ height: '500px', width: 'auto' }}/>
              {routerSprings.map((props, index) => (
                <animated.div
                  key={routers[index].id}
                  style={{ ...props, position: 'absolute', left: routers[index].x, top: routers[index].y, fontSize: '2rem' }}
                >
                  <BsRouter />
                </animated.div>
              ))}
              {lineSprings.map((props, index) => (
                <animated.div
                  key={`line-${routers[index].id}`}
                  style={{ ...props, position: 'absolute', left: routers[index].x, top: routers[index].y, background: 'black', height: '2px', transformOrigin: '0% 50%' }}
                />
              ))}
              <animated.img
                src={shield}
                alt="Shield"
                style={{ ...shieldAnimation, position: 'absolute', left: center.x, top: center.y, transform: 'translate(-50%, -50%)', width: '100px' }}
              />
            </div>
          </Fade>
        </div>
      </div>

      <div className={`container mx-auto p-4 text-center ${isRTL ? 'font-arabic' : ''}`} style={{ color: 'white', backgroundColor: '#111820' }}>
        <h2 className="text-center font-bold text-xl mb-4">{t.antiddos_impact}</h2>
        {t.antiddos_bullets.map((item, index) => (
          <Fade key={index} left={index % 2 === 0} right={index % 2 !== 0} delay={index * 500}>
            <p style={{ fontSize: '1.2em', fontWeight: 'bold', marginBottom: '20px' }}>•{item}•</p>
          </Fade>
        ))}
      </div>

      <div className="bg-white">
        <div className={`container mx-auto p-0 ${isRTL ? 'font-arabic' : ''}`}>
          <h2 className="text-center font-bold text-xl">{t.antiddos_timeline}</h2>
          <VerticalTimeline lineColor="#111820">
            {t.antiddos_events.map(event => (
              <VerticalTimelineElement
                key={event.year}
                className="vertical-timeline-element--work"
                contentStyle={{ background: 'rgb(151, 208, 112)', color: '#fff' }}
                contentArrowStyle={{ borderRight: '7px solid rgb(151, 208, 112)' }}
                date={event.year}
                iconStyle={{ background: 'rgb(151, 208, 112)', color: '#fff' }}
                icon={<FaShieldHalved />}
              >
                <h3 className="vertical-timeline-element-title">{event.title}</h3>
                <p>{event.text}</p>
              </VerticalTimelineElement>
            ))}
          </VerticalTimeline>
        </div>
      </div>
      <div style={{ backgroundColor: '#111820' }}>
        <Footer />
      </div>
    </div>
  );
};

export default AntiDDos;
